import { faBedPulse, faBolt, faCaretDown, faCheckCircle, faChildReaching, faCommentSms, faLaptopMedical, faList, faPlus, faSpinner, faStethoscope, faUser, faVial, faVialCircleCheck, faXRay } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import { Button, Col, Container, OverlayTrigger, Placeholder, Row, Tab, Tabs, Tooltip } from "react-bootstrap";
import { useNavigate, useParams } from 'react-router-dom';
import { callFhirPatient, FhirPatientVisit } from '../../amplify/appsync/fetch-sync-fhir';
import { callQuery, FollowedPatient, GET_FOLLOWED_PATIENTS_BY_USERNAME_AND_CSN, PATIENT_VISIT_BY_INTERNAL_CSN, PatientFileVersion, UNREAD_EXPLAIN_JOBS } from '../../amplify/appsync/query';
import { PatientVisit } from '../../amplify/appsync/search-patients';
import { trackEvent } from '../../amplify/appsync/user-events';
import { callWrite, DELETE_FOLLOWED_PATIENT, INSERT_FOLLOWED_PATIENT } from '../../amplify/appsync/write';
import { AppAuthStateProps } from '../../types/propTypes';
import { getLocalDateTimeStr } from '../../utils/time';
import { UserRole } from '../../utils/user';
import { ChiefComplaint } from '../common/ChiefComplaint';
import { useGlobalOptions } from '../common/GlobalOptionsContext';
import PersonalIdentifier from '../common/PersonalIdentifier';
import { PatientVisitSelector } from '../visit-selector/PatientVisitSelector';
import { Dashboard } from './Dashboard';
import { JOB_CONFIG as ANATOMY_JOB_CONFIG, AnatomyDashboard } from './llm/anatomy/AnatomyDashboard';
import { JOB_CONFIG as ASSESSMENT_JOB_CONFIG, AssessmentDashboard } from './llm/assessment/AssessmentDashboard';
import { CHARTING_JOB_CONFIG, ChartingDashboard } from './llm/charting/ChartingDashboard';
import { EvaluationDashboard } from './llm/evaluation/EvaluationDashboard';
import { LidiaDashboard } from './llm/lidia/LidiaDashboard';
import { JOB_CONFIG as MANAGEMENT_JOB_CONFIG, ManagementDashboard } from './llm/management/ManagementDashboard';
import { JOB_CONFIG as DASHBOARD_JOB_CONFIG } from './llm/notes/MonitorDashboard';
import { JOB_CONFIG as NOTES_JOB_CONFIG, NotesDashboard } from './llm/notes/NotesDashboard';
import { JOB_CONFIG as PHYSIOLOGY_JOB_CONFIG, PhysiologyDashboard } from './llm/physiology/PhysiologyDashboard';
import { JOB_CONFIG as RISK_JOB_CONFIG, RiskDashboard } from './llm/risk/RiskDashboard';

interface DashboardContainerProps extends AppAuthStateProps {
    initialKey?: string;
}

export const DashboardContainer = (props: DashboardContainerProps) => {
    const navigate = useNavigate();

    const [key, setKey] = useState<any>(props.initialKey ?? 'notes');
    const [patientVisit, setPatientVisit] = useState<PatientVisit>();
    const [isFollowing, setIsFollowing] = useState<boolean>(false);
    const [isLoadingFollowing, setIsLoadingFollowing] = useState<boolean>(true);
    const [isSavingFollowing, setIsSavingFollowing] = useState<boolean>(false);
    const [fhirPatientVisit, setFhirPatientVisit] = useState<FhirPatientVisit>();
    const [showPatientVisitSelectorModal, setShowPatientVisitSelectorModal] = useState<any>(false);
    const [unreadFileVersionMap, setUnreadFileVersionMap] = useState<Record<string, PatientFileVersion>>();

    let { internalCSN } = useParams();
    const { getDerivedRole } = useGlobalOptions();

    useEffect(() => {
        if (!internalCSN) return;

        callQuery({
            operation: PATIENT_VISIT_BY_INTERNAL_CSN,
            internal_csn: internalCSN,
        })
        .then(async (patients: PatientVisit[]) => {
            console.log("callQuery returned for internalCSN: " + internalCSN)
            let currPatientVisit: PatientVisit | null = null;
            for (var patient of patients) {
                if (patient.internal_csn === internalCSN) {
                    currPatientVisit = patient;
                }
            }

            if (currPatientVisit) {
                setPatientVisit(currPatientVisit);
                callFhirPatient({
                    mrn: currPatientVisit.mrn,
                }).then((fhirPatientInfo: FhirPatientVisit) => {
                    setFhirPatientVisit(fhirPatientInfo);
                }).catch((err: any) => {
                    console.error("Could not fetch patient info from FHIR", err)
                });

                callQuery({
                    operation: GET_FOLLOWED_PATIENTS_BY_USERNAME_AND_CSN,
                    csn: currPatientVisit.csn,
                    username: props.user.username,
                })
                .then(async (patients: FollowedPatient[]) => {
                    if (patients.length > 0) {
                        setIsFollowing(true);
                    } else {
                        setIsFollowing(false);
                    }
                })
                .finally(() => {
                    setIsLoadingFollowing(false);
                });
            }
        });
    }, [internalCSN]);

    useEffect(() => {
        if (!internalCSN) return;

        if (!patientVisit) return;

        callQuery({
            operation: UNREAD_EXPLAIN_JOBS,
            csn: patientVisit.csn,
        }).then((unreadFileVersions: PatientFileVersion[]) => {
            const fileVersionMap: Record<string, PatientFileVersion> = {};
            for (let fv of unreadFileVersions) {
                fileVersionMap[fv.file_type] = fv;
            }
            setUnreadFileVersionMap(fileVersionMap);
        });
    }, [key, patientVisit]);

    // Sync tab selection with URL
    useEffect(() => {
        if (props.initialKey !== key) {
            setKey(props.initialKey || 'notes');
        }
    }, [props.initialKey]);

    const handleSelect = (k: string | null) => {
        if (k) {
            navigate(`/dashboard/${internalCSN}/${k}`);
        }
    };

    const formatPatientName = () => {
        if (fhirPatientVisit) {
            if (fhirPatientVisit.family) {
                return <>{`${fhirPatientVisit.family + ", " + fhirPatientVisit.given}`}</>
            }
        }
        return <>-</>;
    }

    const followPatient = () => {
        setIsSavingFollowing(true);
        if (!patientVisit) {
            return;
        }
        if (isFollowing) {
            callWrite({
                operation: DELETE_FOLLOWED_PATIENT,
                csn: patientVisit.csn,
                username: props.user.username,
            })
            .then(() => {
                setIsFollowing(false);
            })
            .finally(() => {
                setIsSavingFollowing(false);
            });

            trackEvent(`unfollow?internal_csn=${patientVisit.internal_csn}`, props.user?.username);
        } else {
            callWrite({
                operation: INSERT_FOLLOWED_PATIENT,
                csn: patientVisit.csn,
                username: props.user.username,
                follow_type: "MANUAL",
            })
            .then(() => {
                setIsFollowing(true);
            })
            .finally(() => {
                setIsSavingFollowing(false);
            });

            trackEvent(`follow?internal_csn=${patientVisit.internal_csn}`, props.user?.username);
        }
    };

    const DASHBOARD_CONFIGS: Record<string, { file: string }[]> = {
        notes: NOTES_JOB_CONFIG,
        charting: CHARTING_JOB_CONFIG,
        management: MANAGEMENT_JOB_CONFIG,
        risk: RISK_JOB_CONFIG,
        assessments: ASSESSMENT_JOB_CONFIG,
        physiology: PHYSIOLOGY_JOB_CONFIG,
        anatomy: ANATOMY_JOB_CONFIG,
        overview: DASHBOARD_JOB_CONFIG,
    };

    const getNumSectionsWithUpdate = (dashboardId: string): string => {
        const configs = DASHBOARD_CONFIGS[dashboardId];
        if (!configs) return "";

        const updates = configs.reduce(
            (count, config) =>
            unreadFileVersionMap?.[config.file] ? count + 1 : count,
            0
        );

        if (updates > 0) {
            return ` (${updates})`
        } else {
            return "";
        }
    };

    if (internalCSN) {
        return (
            <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                <div
                    style={{marginTop: '60px'}}
                >
                    {
                        patientVisit && (
                            <Container fluid>
                                <Row>
                                    <Col className="d-flex">
                                        <h6 className={fhirPatientVisit ? "fade-in" : ""}>
                                            <OverlayTrigger
                                                placement="bottom"
                                                overlay={
                                                    <Tooltip>
                                                        Show Past Visits
                                                    </Tooltip>
                                                }
                                            >
                                                <span className="dashboard-patient-selector" onClick={() => {setShowPatientVisitSelectorModal(true)}}><span className="me-1"><FontAwesomeIcon icon={faUser} /></span> <PersonalIdentifier identifier={formatPatientName()} /> <FontAwesomeIcon icon={faCaretDown} /></span>
                                            </OverlayTrigger>
                                        </h6>
                                    </Col>
                                </Row>
                                <PatientVisitSelector 
                                    user={props.user}
                                    ws={props.ws}
                                    showPatientVisitSelectorModal={showPatientVisitSelectorModal} 
                                    setShowPatientVisitSelectorModal={setShowPatientVisitSelectorModal} 
                                    patientVisit={patientVisit}
                                    width={1024}
                                />
                                {
                                    patientVisit ? (
                                        <Row>
                                            <Col md={12} className="notes-metadata">
                                                CSN <PersonalIdentifier identifier={patientVisit.csn} /> &bull; MRN <PersonalIdentifier identifier={patientVisit.mrn} /> &bull; <>Age</> {patientVisit.age} &bull; <>Sex</> {patientVisit.gender} &bull; <>Bed</> {patientVisit.bed} &bull; Arrival: <PersonalIdentifier identifier={getLocalDateTimeStr(patientVisit.arrival_time)} /> {patientVisit.departure_time && <>&bull; Departed: <PersonalIdentifier identifier={getLocalDateTimeStr(patientVisit.departure_time)} /></>} &bull; <>CC</> <ChiefComplaint patientVisit={patientVisit} />
                                            </Col>
                                        </Row>
                                    ) : (
                                        <Row>
                                            <Col>
                                                <Placeholder animation="glow">
                                                    <Placeholder xs={4} />
                                                </Placeholder>
                                            </Col>
                                        </Row>
                                    )
                                }
                            </Container>
                        )
                    }
                </div>
                <Tabs
                    activeKey={key}
                    onSelect={handleSelect}
                    className="modern-tabs mt-3"
                >
                    <Tab eventKey="notes" title={
                        <>
                            <FontAwesomeIcon icon={faStethoscope} className="me-2" />
                            Workup{getNumSectionsWithUpdate('notes')}
                        </>
                    }>
                    </Tab>
                    <Tab eventKey="charting" title={
                        <>
                            <FontAwesomeIcon icon={faList} className="me-2" />
                            Charting{getNumSectionsWithUpdate('charting')}
                        </>
                    }>
                    </Tab>
                    <Tab eventKey="overview" title={
                        <>
                            <FontAwesomeIcon icon={faBedPulse} className="me-2" />
                            Monitoring{getNumSectionsWithUpdate('overview')}
                        </>
                    }>
                    </Tab>
                    <Tab eventKey="management" title={
                        <>
                            <FontAwesomeIcon icon={faLaptopMedical} className="me-2" />
                            Management{getNumSectionsWithUpdate('management')}
                        </>
                    }>
                    </Tab>
                    <Tab eventKey="anatomy" title={
                        <>
                            <FontAwesomeIcon icon={faXRay} className="me-2" />
                            Anatomy{getNumSectionsWithUpdate('anatomy')}
                        </>
                    }>
                    </Tab>
                    <Tab eventKey="physiology" title={
                        <>
                            <FontAwesomeIcon icon={faChildReaching} className="me-2" />
                            Physiology{getNumSectionsWithUpdate('physiology')}
                        </>
                    }>
                    </Tab>
                    <Tab eventKey="risk" title={
                        <>
                            <FontAwesomeIcon icon={faBolt} className="me-2" />
                            Risk{getNumSectionsWithUpdate('risk')}
                        </>
                    }>
                    </Tab>
                    <Tab eventKey="assessments" title={
                        <>
                            <FontAwesomeIcon icon={faVial} className="me-2" />
                            Assessments{getNumSectionsWithUpdate('assessments')}
                        </>
                    }>
                    </Tab>
                    {
                        getDerivedRole(props.user) === UserRole.ADMIN && (
                            <Tab eventKey="lidia" title={
                                <>
                                    <FontAwesomeIcon icon={faCommentSms} className="me-2" />
                                    LIDIA
                                </>
                            }>
                            </Tab>
                        )
                    }
                    {
                        getDerivedRole(props.user) === UserRole.ADMIN && (
                            <Tab eventKey="evaluation" title={
                                <>
                                    <FontAwesomeIcon icon={faVialCircleCheck} className="me-2" />
                                    Capacity Evaluation
                                </>
                            }>
                            </Tab>
                        )
                    }
                </Tabs>
                {
                    !isLoadingFollowing && (
                        <div className="dashboard-follow">
                            <OverlayTrigger
                                placement="bottom"
                                overlay={
                                    <Tooltip>
                                        {!isFollowing ? "Follow Patient" : "Unfollow Patient"}
                                    </Tooltip>
                                }
                            >
                                <Button size="sm" variant={isFollowing ? "success" : "outline-secondary"} onClick={followPatient}>
                                    <span className="me-1">{isSavingFollowing ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={isFollowing ? faCheckCircle : faPlus} />}</span> {isFollowing ? "Following" : "Follow"}
                                </Button>
                            </OverlayTrigger>
                        </div>
                    )
                }
                {
                    key === "overview" && (
                        <div style={{ flex: 1, overflowY: 'auto' }} className='pt-3'>
                            <Dashboard
                                {...props}
                                patientVisit={patientVisit}
                            />
                        </div>
                    )
                }
                {
                    key === "notes" && (
                        <div style={{ flex: 1, overflowY: 'auto' }} className='pt-3'>
                            <NotesDashboard
                                {...props}
                                patientVisit={patientVisit}
                                unreadFileVersionMap={unreadFileVersionMap}
                                setUnreadFileVersionMap={setUnreadFileVersionMap}
                            />
                        </div>
                    )
                }
                {
                    key === "charting" && (
                        <div style={{ flex: 1, overflowY: 'auto' }} className='pt-3'>
                            <ChartingDashboard
                                {...props}
                                patientVisit={patientVisit}
                                unreadFileVersionMap={unreadFileVersionMap}
                                setUnreadFileVersionMap={setUnreadFileVersionMap}
                            />
                        </div>
                    )
                }
                {
                    key === "management" && (
                        <div style={{ flex: 1, overflowY: 'auto' }} className='pt-3'>
                            <ManagementDashboard
                                {...props}
                                patientVisit={patientVisit}
                                unreadFileVersionMap={unreadFileVersionMap}
                                setUnreadFileVersionMap={setUnreadFileVersionMap}
                            />
                        </div>
                    )
                }
                {
                    key === "anatomy" && (
                        <div style={{ flex: 1, overflowY: 'auto' }} className='pt-3'>
                            <AnatomyDashboard
                                {...props}
                                patientVisit={patientVisit}
                                unreadFileVersionMap={unreadFileVersionMap}
                                setUnreadFileVersionMap={setUnreadFileVersionMap}
                            />
                        </div>
                    )
                }
                {
                    key === "physiology" && (
                        <div style={{ flex: 1, overflowY: 'auto' }} className='pt-3'>
                            <PhysiologyDashboard
                                {...props}
                                patientVisit={patientVisit}
                                unreadFileVersionMap={unreadFileVersionMap}
                                setUnreadFileVersionMap={setUnreadFileVersionMap}
                            />
                        </div>
                    )
                }
                {
                    key === "risk" && (
                        <div style={{ flex: 1, overflowY: 'auto' }} className='pt-3'>
                            <RiskDashboard
                                {...props}
                                patientVisit={patientVisit}
                                unreadFileVersionMap={unreadFileVersionMap}
                                setUnreadFileVersionMap={setUnreadFileVersionMap}
                            />
                        </div>
                    )
                }
                {
                    key === "assessments" && (
                        <div style={{ flex: 1, overflowY: 'auto' }} className='pt-3'>
                            <AssessmentDashboard
                                {...props}
                                patientVisit={patientVisit}
                                unreadFileVersionMap={unreadFileVersionMap}
                                setUnreadFileVersionMap={setUnreadFileVersionMap}
                            />
                        </div>
                    )
                }
                {
                    key === "lidia" && (
                        <div style={{ flex: 1, overflowY: 'auto' }} className='pt-3'>
                            <LidiaDashboard
                                {...props}
                                patientVisit={patientVisit}
                                showHeader={true}
                            />
                        </div>
                    )
                }
                {
                    key === "evaluation" && (
                        <div style={{ flex: 1, overflowY: 'auto' }} className='pt-3'>
                            <EvaluationDashboard
                                {...props}
                                patientVisit={patientVisit}
                                unreadFileVersionMap={unreadFileVersionMap}
                                setUnreadFileVersionMap={setUnreadFileVersionMap}
                            />
                        </div>
                    )
                }
            </div>
        );
    } else {
        return (
            <Dashboard
                {...props}
            />
        )
    }
};
